import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { HINTS_TOTAL_STEPS } from '../constants';

const MainBodyLayout = ({
  children = null,
  withBack = false,
  onPrevClick = null,
  withInfoIcon = false,
  title = null,
  className = '',
}) => {
  const { state, openTerms, updateHintsVisibility } = useContext(CryptodraftContext);

  const hintsOpened = state?.hintsOpened;

  const { t: translate } = useTranslation();
  const history = useHistory();

  const closeCryptodraft = () => history.push('/dashboard/card/details');

  return (
    <div className={`d-flex flex-column main-layout-body-container ${className} main-body-layout`}>
      {(withBack || withInfoIcon) && (
        <div className="d-flex flex-row justify-content-between align-items-center">
          {withBack && (
            <div
              className="cursor-pointer text-hover"
              onClick={onPrevClick}
              onKeyDown={onPrevClick}
              role="presentation">
              <i className="icon icon-arrow_back-navigation text-f5 me-2" aria-label="Back" />
            </div>
          )}
          {!withBack && withInfoIcon && (
            <>
              {/* mobile */}
              <p className="d-block d-md-none text-dark text-f4">{title}</p>

              {/* browser */}
              <p className="d-none d-md-block text-dark text-f3">{title}</p>
            </>
          )}
          <div className="d-flex flex-row align-items-center ">
            {withInfoIcon && (
              <>
                <div
                  data-tooltip-id="cryptodraft-hint-1"
                  className="cursor-pointer text-hover"
                  onClick={openTerms}
                  onKeyDown={openTerms}
                  role="presentation">
                  {/* mobile */}
                  <i
                    className="d-block d-md-none icon icon-question-other text-shadow"
                    style={{ fontSize: '1.7rem' }}
                  />

                  {/* browser */}
                  <i
                    className="d-none d-md-block icon icon-question-other text-shadow"
                    style={{ fontSize: '1.2rem' }}
                  />
                </div>
              </>
            )}
            {/* mobile only */}
            <div
              className="d-md-none"
              onClick={closeCryptodraft}
              onKeyDown={closeCryptodraft}
              role="button"
              tabIndex={-1}>
              <i
                className="close-btn cursor-pointer icon icon-close-other text-dark text-f4 ms-2"
                aria-label="Close"
              />
            </div>
          </div>
        </div>
      )}
      {withBack && withInfoIcon && title && (
        <div className="d-flex flex-row justify-content-start align-items-center mt-3 mt-md-4">
          {/* mobile */}
          <p className="d-block d-md-none text-dark text-f4">{title}</p>

          {/* browser */}
          <p className="text-dark d-none d-md-block text-f3">{title}</p>
        </div>
      )}
      {children}
      <Tooltip
        isOpen={hintsOpened?.first}
        id="cryptodraft-hint-1"
        place="right"
        variant="dark"
        opacity={1}
        clickable
        className="p-4 rounded border-1 custom-tooltip">
        <div>
          <p className="text-white text-f6" style={{ opacity: '0.5' }}>
            {translate('hint_step')
              .replace('__CURRENTSTEP__', 1)
              .replace('__TOTAL__', HINTS_TOTAL_STEPS)}
          </p>
          <p className="text-white text-f6 mt-2">{translate('create_cryptodraft_hint_1')}</p>
          <div className="d-flex flex-row justify-content-end align-items-center mt-1">
            <button
              className="btn btn-light py-1 px-3 text-f5"
              style={{ borderRadius: '10px' }}
              type="button"
              onClick={() =>
                updateHintsVisibility({
                  first: false,
                  second: true,
                  third: false,
                })
              }>
              {translate('next')}
            </button>
          </div>
        </div>
      </Tooltip>
      <Tooltip
        isOpen={hintsOpened?.second}
        id="cryptodraft-hint-2"
        place="right"
        variant="dark"
        opacity={1}
        clickable
        className="p-4 rounded border-1 custom-tooltip">
        <div>
          <p className="d-none d-md-block text-white text-f6" style={{ opacity: '0.5' }}>
            {translate('hint_step')
              .replace('__CURRENTSTEP__', 2)
              .replace('__TOTAL__', HINTS_TOTAL_STEPS)}
          </p>
          <p className="text-white text-f6 mt-2">{translate('create_cryptodraft_hint_2')}</p>
          <div className="d-flex flex-row justify-content-end align-items-center mt-1">
            <button
              className="btn btn-light py-1 px-3 text-f5"
              style={{ borderRadius: '10px' }}
              type="button"
              onClick={() =>
                updateHintsVisibility({
                  first: false,
                  second: false,
                  third: true,
                })
              }>
              {translate('next')}
            </button>
          </div>
        </div>
      </Tooltip>
      <Tooltip
        isOpen={hintsOpened?.third}
        id="cryptodraft-hint-3"
        place="right"
        variant="dark"
        opacity={1}
        clickable
        className="p-4 rounded border-1 custom-tooltip">
        <div>
          <p className="d-none d-md-block text-white text-f6" style={{ opacity: '0.5' }}>
            {translate('hint_step')
              .replace('__CURRENTSTEP__', 3)
              .replace('__TOTAL__', HINTS_TOTAL_STEPS)}
          </p>
          <p className="text-white text-f6 mt-2">{translate('create_cryptodraft_hint_3')}</p>
          <div className="d-flex flex-row justify-content-end align-items-center mt-1">
            <button
              className="btn btn-light py-1 px-3 text-f5"
              style={{ borderRadius: '10px' }}
              type="button"
              onClick={() =>
                updateHintsVisibility({
                  first: false,
                  second: false,
                  third: false,
                })
              }>
              {translate('got_it')}
            </button>
          </div>
        </div>
      </Tooltip>
    </div>
  );
};

export default MainBodyLayout;

MainBodyLayout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  withBack: PropTypes.bool,
  withInfoIcon: PropTypes.bool,
  title: PropTypes.string,
  className: PropTypes.string,
  onPrevClick: PropTypes.func,
};
